import {
  CSSProperties, PointerEventHandler, ReactNode, useEffect, useState,
} from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DashboardWidgetId,
  DASHBOARD_WIDGETS,
} from '../../../pages/Home/dashboardWidgets';
import DashboardWidgetMenu from '../DashboardWidgetMenu/DashboardWidgetMenu';
import S from './DashboardMovableGridStyles';

type WidgetActions = {
  onMove: () => void;
  onRemove: () => void;
};

type Props = {
  widgetIds: DashboardWidgetId[];
  onMove: (activeId: DashboardWidgetId, overId: DashboardWidgetId) => void;
  onRemove: (widgetId: DashboardWidgetId) => void;
  renderWidget: (widgetId: DashboardWidgetId, actions: WidgetActions) => ReactNode;
};

type SortableWidgetProps = {
  widgetId: DashboardWidgetId;
  isMoveActive: boolean;
  onMoveRequested: () => void;
  onRemove: () => void;
  children: ReactNode;
};

function SortableWidget({
  widgetId,
  isMoveActive,
  onMoveRequested,
  onRemove,
  children,
}: SortableWidgetProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widgetId });
  const definition = DASHBOARD_WIDGETS[widgetId];
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    gridColumn: `span ${definition.columnSpan}`,
    gridRow: `span ${definition.rowSpan}`,
    zIndex: isDragging ? 2 : undefined,
  };
  const onPointerDown = isMoveActive
    ? listeners?.onPointerDown as PointerEventHandler<HTMLDivElement>
    : undefined;
  return (
    <S.WidgetSlot
      ref={setNodeRef}
      style={style}
      className={[
        definition.hasInternalMenu ? '' : 'with-surface',
        isMoveActive ? 'move-active' : '',
        isDragging ? 'dragging' : '',
      ].filter(Boolean).join(' ')}
      role={isMoveActive ? attributes.role : undefined}
      tabIndex={isMoveActive ? attributes.tabIndex : undefined}
      aria-disabled={isMoveActive ? attributes['aria-disabled'] : undefined}
      aria-pressed={isMoveActive ? attributes['aria-pressed'] : undefined}
      aria-roledescription={isMoveActive ? attributes['aria-roledescription'] : undefined}
      aria-describedby={isMoveActive ? attributes['aria-describedby'] : undefined}
      onPointerDown={onPointerDown}
    >
      {!definition.hasInternalMenu && (
        <div className="external-widget-menu">
          <DashboardWidgetMenu onMove={onMoveRequested} onRemove={onRemove} />
        </div>
      )}
      {children}
      {isMoveActive && <div className="move-instruction">Drag to a new position</div>}
    </S.WidgetSlot>
  );
}

function DashboardMovableGrid({
  widgetIds,
  onMove,
  onRemove,
  renderWidget,
}: Props): JSX.Element {
  const [moveWidgetId, setMoveWidgetId] = useState<DashboardWidgetId | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  }));

  useEffect(() => {
    if (moveWidgetId && !widgetIds.includes(moveWidgetId)) {
      setMoveWidgetId(null);
    }
  }, [moveWidgetId, widgetIds]);

  useEffect(() => {
    if (!moveWidgetId) {
      return undefined;
    }

    const cancelMove = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMoveWidgetId(null);
      }
    };

    window.addEventListener('keydown', cancelMove);
    return () => window.removeEventListener('keydown', cancelMove);
  }, [moveWidgetId]);

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (over && active.id !== over.id) {
      onMove(active.id as DashboardWidgetId, over.id as DashboardWidgetId);
    }
    setMoveWidgetId(null);
  };

  if (widgetIds.length === 0) {
    return <S.EmptyState>No widgets added</S.EmptyState>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragCancel={() => setMoveWidgetId(null)}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={widgetIds} strategy={rectSortingStrategy}>
        <S.Grid>
          {widgetIds.map((widgetId) => {
            const actions = {
              onMove: () => setMoveWidgetId((current) => (
                current === widgetId ? null : widgetId
              )),
              onRemove: () => onRemove(widgetId),
            };

            return (
              <SortableWidget
                key={widgetId}
                widgetId={widgetId}
                isMoveActive={moveWidgetId === widgetId}
                onMoveRequested={actions.onMove}
                onRemove={actions.onRemove}
              >
                {renderWidget(widgetId, actions)}
              </SortableWidget>
            );
          })}
        </S.Grid>
      </SortableContext>
    </DndContext>
  );
}

export default DashboardMovableGrid;
